import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { i18n, loadAndSetLocale } from './i18n/index'
import router from './router'
import App from './App.vue'
import { loadConfigurationAsync } from './composables/Configuration'
import { useAppStore } from './stores/appStore'
import { useAidaSessionStore } from './stores/aidaSessionStore'
import type { Configuration } from './types/configuration/Configuration'
import './style.css'
import {
  initializeAidaLibraryAsync,
  loadAidaLibraryFromExternalUrl,
} from './composables/ChatCommunication'
import { resolveAidaClientConfig } from './composables/AidaClientConfig'
import hljsVuePlugin from '@highlightjs/vue-plugin'

import './interceptors/axiosInterceptor'
console.info(
  `[AIDA_GENERIC_CLIENT] 🚀 App ${__APP_NAME__} v${__APP_VERSION__} built at ${__BUILD_DATE__}`
)

const app = createApp(App)
const pinia = createPinia()

app.use(pinia).use(i18n)

const appStore = useAppStore()
const aidaSessionStore = useAidaSessionStore()
const config = await loadConfigurationAsync()

if (config) {
  appStore.setConfiguration(config)
} else {
  console.warn('Failed to load application configuration.')
}

await applyStartupLanguagePreference(config)

const aidaClientConfig = resolveAidaClientConfig(appStore.selectedLanguage)

loadAidaLibraryFromExternalUrl()

let aidaLibrary = await initializeAidaLibraryAsync(aidaClientConfig)

aidaSessionStore.setAidaClient(aidaLibrary, aidaClientConfig.name, aidaClientConfig.key)

app.use(router)
app.use(hljsVuePlugin)
await router.isReady()
app.mount('#app')

async function applyStartupLanguagePreference(config: Configuration | null): Promise<void> {
  const languageQuery = new URLSearchParams(window.location.search).get('lang') ?? ''
  const normalizedLanguageQuery = languageQuery.toLowerCase()
  const languageFromUrlEnabled = !!config?.components.languageFromUrl?.enabled
  const availableLanguages = config?.languages.map(lang => lang.fileName) ?? []

  if (
    languageFromUrlEnabled &&
    normalizedLanguageQuery &&
    availableLanguages.includes(normalizedLanguageQuery)
  ) {
    const languageApplied = await loadAndSetLocale(normalizedLanguageQuery)

    if (languageApplied) {
      appStore.setLanguage(normalizedLanguageQuery)
      return
    }

    console.log(`No such language or translation unavailable for lang parameter: ${languageQuery}`)
  }

  await loadAndSetLocale(appStore.selectedLanguage)
}
